'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { College } from '@/lib/services/collegeService';
import AppImage from '@/components/ui/AppImage';
import { MapPin, Star, GitCompare, BookOpen, Building2, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

interface CollegeCardProps {
  college: College;
  isCompared: boolean;
  onToggleCompare: () => void;
  viewMode: 'grid' | 'list';
}

function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating / 2);
  const half = (rating / 2) % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={`star-${i}`}
          size={13}
          className={i < full ? 'text-amber-400 fill-amber-400' : i === full && half ? 'text-amber-400' : 'text-slate-300'}
          fill={i < full ? 'currentColor' : 'none'}
        />
      ))}
    </div>
  );
}

function AccreditationBadge({ acc }: { acc: string }) {
  const cls =
    acc === 'NAAC A++' ? 'naac-a-plus-plus' :
    acc === 'NAAC A+' ? 'naac-a-plus' :
    acc === 'NAAC A'? 'naac-a' : 'naac-b-plus-plus';
  return <span className={`naac-badge ${cls}`}>{acc}</span>;
}

export default function CollegeCard({ college, isCompared, onToggleCompare, viewMode }: CollegeCardProps) {
  const [showCourses, setShowCourses] = useState(false);

  const formatFees = (amount: number) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  const formatPackage = (amount: number) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  const streamBadgeClass =
    college.streams[0] === 'Engineering' ? 'stream-badge-engineering' :
    college.streams[0] === 'Medical' ? 'stream-badge-medical' :
    college.streams[0] === 'Management' ? 'stream-badge-management' :
    college.streams[0] === 'Law' ? 'stream-badge-law' :
    college.streams[0] === 'Arts'? 'stream-badge-arts' : 'stream-badge-commerce';

  if (viewMode === 'list') {
    return (
      <div className={`card-base card-hover overflow-hidden animate-fade-in ${isCompared ? 'ring-2 ring-primary' : ''}`}>
        <div className="flex gap-0">
          {/* Image strip */}
          <div className="relative w-32 shrink-0">
            <AppImage
              src={college.image}
              alt={`${college.name} campus building`}
              fill
              className="object-cover"
              sizes="128px"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
          </div>

          <div className="flex-1 p-4 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {college.nirfRank && <span className="nirf-badge">NIRF #{college.nirfRank}</span>}
                  <AccreditationBadge acc={college.accreditation} />
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${streamBadgeClass}`}>
                    {college.streams[0]}
                  </span>
                </div>
                <h3 className="font-bold text-base text-foreground truncate">{college.name}</h3>
                <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
                  <MapPin size={12} />
                  <span className="text-xs">{college.location}, {college.state}</span>
                  <span className="text-xs ml-2 text-muted-foreground">Est. {college.established}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-lg font-bold text-foreground font-tabular">{college.rating}</span>
                <div>
                  <StarRating rating={college.rating} />
                  <span className="text-xs text-muted-foreground font-tabular">{college.reviewCount.toLocaleString()} reviews</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-border">
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Total Fees</p>
                <p className="text-sm font-bold text-foreground font-tabular">{formatFees(college.totalFees)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Cutoff ({college.cutoffExam})</p>
                <p className="text-sm font-bold text-primary font-tabular">
                  {college.cutoffScore} {college.cutoffUnit}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Avg Package</p>
                <p className="text-sm font-bold text-foreground font-tabular">{formatPackage(college.placementAvgPackage)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3">
              <button onClick={onToggleCompare} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${isCompared ? 'bg-primary/10 text-primary border-primary' : 'border-border text-muted-foreground hover:border-primary hover:text-primary'}`}>
                <GitCompare size={13} />
                {isCompared ? 'Added' : 'Compare'}
              </button>
              <Link href={`/college/${college.id}`} className="btn-secondary text-xs py-1.5 px-3 flex-1 text-center">
                View Details
              </Link>
              <Link href="/college-predictor" className="btn-primary text-xs py-1.5 px-3 flex-1 text-center">
                Check Chances
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card-base card-hover overflow-hidden animate-fade-in flex flex-col ${isCompared ? 'ring-2 ring-primary' : ''}`}>
      {/* Image */}
      <div className="relative h-44 shrink-0">
        <AppImage
          src={college.image}
          alt={`${college.name} main campus entrance`}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 50vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Badges on image */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
          {college.nirfRank && <span className="nirf-badge shadow">NIRF #{college.nirfRank}</span>}
          <AccreditationBadge acc={college.accreditation} />
        </div>

        {/* Rating on image */}
        <div className="absolute top-3 right-3 bg-white/95 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 shadow">
          <span className="text-sm font-bold text-foreground font-tabular">{college.rating}</span>
          <Star size={13} className="text-amber-400 fill-amber-400" />
        </div>

        {/* College name overlay */}
        <div className="absolute bottom-3 left-3 right-3">
          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-1.5 ${streamBadgeClass} bg-white/90`}>
            {college.streams[0]}
          </span>
          <h3 className="font-bold text-white text-base leading-tight line-clamp-2 drop-shadow">
            {college.name}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        {/* Location + ownership */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin size={13} />
            <span className="text-xs">{college.location}, {college.state}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Building2 size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{college.ownership}</span>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 p-3 bg-muted rounded-lg mb-3">
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-0.5 leading-tight">Total Fees</p>
            <p className="text-sm font-bold text-foreground font-tabular">{formatFees(college.totalFees)}</p>
          </div>
          <div className="text-center border-x border-border">
            <p className="text-xs text-muted-foreground mb-0.5 leading-tight">Cutoff</p>
            <p className="text-sm font-bold text-primary font-tabular">
              {college.cutoffScore}
              <span className="text-xs font-normal ml-0.5 text-muted-foreground">{college.cutoffUnit}</span>
            </p>
            <p className="text-xs text-muted-foreground leading-tight">{college.cutoffExam}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-0.5 leading-tight">Avg Pkg</p>
            <p className="text-sm font-bold text-foreground font-tabular">{formatPackage(college.placementAvgPackage)}</p>
          </div>
        </div>

        {/* Reviews row */}
        <div className="flex items-center gap-2 mb-3">
          <StarRating rating={college.rating} />
          <span className="text-xs text-muted-foreground font-tabular">{college.reviewCount.toLocaleString()} reviews</span>
          {college.hostel && (
            <span className="flex items-center gap-1 text-xs text-green-600 ml-auto">
              <CheckCircle2 size={12} />
              Hostel
            </span>
          )}
          {college.scholarships && (
            <span className="flex items-center gap-1 text-xs text-blue-600">
              <CheckCircle2 size={12} />
              Scholarship
            </span>
          )}
        </div>

        {/* Courses toggle */}
        <button
          onClick={() => setShowCourses(!showCourses)}
          className="flex items-center justify-between text-xs text-primary font-semibold mb-2 hover:opacity-80 transition-opacity"
        >
          <span className="flex items-center gap-1.5">
            <BookOpen size={13} />
            {college.courses.length} Courses & Fees
          </span>
          {showCourses ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showCourses && (
          <div className="mb-3 border border-border rounded-lg overflow-hidden animate-fade-in">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Course</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Level</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground">Fees</th>
                </tr>
              </thead>
              <tbody>
                {college.courses.map((course, ci) => (
                  <tr key={`course-${college.id}-${course.id}`} className={ci % 2 === 0 ? 'bg-card' : 'bg-muted/30'}>
                    <td className="px-3 py-1.5 font-medium text-foreground">{course.name}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${course.level === 'UG' ? 'bg-blue-50 text-blue-700' : course.level === 'PG' ? 'bg-purple-50 text-purple-700' : 'bg-orange-50 text-orange-700'}`}>
                        {course.level}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-bold text-foreground font-tabular">{formatFees(course.totalFees)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-auto pt-1">
          <button
            onClick={onToggleCompare}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-all ${
              isCompared
                ? 'bg-primary/10 text-primary border-primary' :'border-border text-muted-foreground hover:border-primary hover:text-primary'
            }`}
          >
            <GitCompare size={13} />
            {isCompared ? 'Added' : 'Compare'}
          </button>
          <Link href={`/college/${college.id}`} className="btn-secondary text-xs py-2 px-3 flex-1 text-center">
            View Details
          </Link>
          <Link href="/college-predictor" className="btn-primary text-xs py-2 px-3 flex-1 text-center">
            Check Chances
          </Link>
        </div>
      </div>
    </div>
  );
}